import { OpeningDetailResponse } from 'api-clients/monolith/models/OpeningDetailResponse';
import _round from 'lodash/round';
import { Loader2 } from 'lucide-react';
import { useCallback, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { ExternalLinkIcon } from '@/components/icons/external-link';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useAccountSlug } from '@/hooks/use-account-slug';
import { useBudgetType } from '@/hooks/use-budget-type';
import { domainMeta } from '@/utils/domain-meta';

import { JobDetailFormState } from '../types';
import { DescriptionSection } from './description-section';
import { PostingTitle } from './posting-title';
import { useJobConstants } from './use-job-constants';
import { useUpdateOpeningDescription } from './use-update-opening-description';
interface ExpandedViewProps {
  opening: OpeningDetailResponse;
  payDisplay: string | null;
  toggleExpanded?: () => void;
  onRequestRefreshBanner?: () => void;
}

export function ExpandedView({
  opening,
  payDisplay,
  toggleExpanded: _toggleExpanded,
  onRequestRefreshBanner,
}: ExpandedViewProps) {
  const intl = useIntl();
  const accountSlug = useAccountSlug();
  const { getJobType, getJobHours } = useJobConstants();
  const { hookForm, selectedOpeningIds, setSelectedOpeningIds } =
    useJobPostingContext();
  const { calculateTotalBudget } = useBudgetType();

  const {
    setValue,
    formState: { errors },
    getValues,
  } = hookForm;
  const hasError = !!errors?.jobDetails?.[opening.external_id]?.description;

  const jobSettingsUrl = `${domainMeta.monolithOrigin}/${accountSlug}/openings/${opening.slug}`;
  const locationSettingsUrl = `${domainMeta.monolithOrigin}/${accountSlug}/locations#/${opening.location_external_id}/edit`;

  /** Capture snapshot of form state when component mounts to allow for canceling the edit. */
  const descriptionSnapshotRef = useRef<JobDetailFormState>(
    JSON.parse(
      JSON.stringify(getValues(`jobDetails.${opening.external_id}`)),
    ) as JobDetailFormState,
  );

  /** Remove the opening from the posting by removing the opening from the selectedOpeningIds and the jobDetails list */
  const removeOpeningFromPosting = useCallback(() => {
    setSelectedOpeningIds(
      selectedOpeningIds.filter(id => id !== opening.external_id),
    );

    // Remove the opening from the jobDetails list
    const jobDetailsList = getValues('jobDetails');
    const updatedJobDetailsList = { ...jobDetailsList };
    delete updatedJobDetailsList[opening.external_id];
    setValue('jobDetails', updatedJobDetailsList);

    // Remove the opening from all the channels openingBudgets list
    const channels = getValues('channels');
    Object.keys(channels).forEach(channelSlug => {
      const openingBudgets = getValues(
        `channels.${channelSlug}.openingBudgets`,
      );
      const updatedOpeningBudgets = { ...openingBudgets };
      delete updatedOpeningBudgets[opening.external_id];
      setValue(`channels.${channelSlug}.openingBudgets`, updatedOpeningBudgets);

      // Recalculate the channel budget by summing the remaining opening budgets
      const recalculatedChannelBudget = Object.values(
        updatedOpeningBudgets,
      ).reduce((acc: number, budget: number | null) => acc + (budget ?? 0), 0);
      setValue(
        `channels.${channelSlug}.channelBudget`,
        _round(recalculatedChannelBudget, 2),
      );

      // Recalculate the total channel budget based on the budget type
      const totalChannelBudget = calculateTotalBudget({
        budgetType: getValues(`channels.${channelSlug}.budgetType`),
        budgetPerUnit: recalculatedChannelBudget,
        startDate: getValues(`channels.${channelSlug}.startsAt`),
        endDate: getValues(`channels.${channelSlug}.endAt`),
      });
      setValue(
        `channels.${channelSlug}.totalChannelBudget`,
        totalChannelBudget,
      );
    });
  }, [
    setSelectedOpeningIds,
    selectedOpeningIds,
    getValues,
    opening.external_id,
    setValue,
    calculateTotalBudget,
  ]);

  const collapseOpening = () => {
    setValue(`jobDetails.${opening.external_id}.isExpanded`, false);
  };

  const { updateOpeningDescription, isUpdatingOpening } =
    useUpdateOpeningDescription({
      onSuccess: () => {
        collapseOpening();

        // Update the snapshot with the current values
        descriptionSnapshotRef.current = JSON.parse(
          JSON.stringify(getValues(`jobDetails.${opening.external_id}`)),
        ) as JobDetailFormState;
      },
    });

  const handleSaveClick = () => {
    updateOpeningDescription({
      openingExternalId: opening.external_id,
      requestBody: {
        description: getValues(`jobDetails.${opening.external_id}.description`),
      },
    });
  };

  const handleCancelClick = () => {
    setValue(
      `jobDetails.${opening.external_id}`,
      descriptionSnapshotRef.current,
    );
    collapseOpening();
  };

  return (
    <div className="border-fx-neutral-200 mx-4 mb-2 flex flex-col gap-6 rounded-lg border bg-white py-6 px-12 shadow-sm md:w-[720px]">
      {/* Posting title */}
      <PostingTitle openingId={opening.external_id} />

      <div className="bg-fx-neutral-200 h-px w-full" />

      {/* Job type, shift, pay range, industry, job hours */}
      <div className="grid auto-cols-auto grid-flow-col items-center gap-6">
        <PropertyItem
          title={intl.formatMessage({
            defaultMessage: 'Industry',
            description: 'Industry of the job',
          })}
          value={opening.job_industry}
        />

        <PropertyItem
          title={intl.formatMessage({
            defaultMessage: 'Job hours',
            description: 'Job hours of the job',
          })}
          value={getJobHours(opening.job_hours)}
        />

        <PropertyItem
          title={intl.formatMessage({
            defaultMessage: 'Job type',
            description: 'Job type of the job',
          })}
          value={getJobType(opening.job_type)}
        />

        <PropertyItem
          title={intl.formatMessage({
            defaultMessage: 'Shift',
            description: 'Shift of the job',
          })}
          value={opening.shift}
        />

        <PropertyItem
          title={intl.formatMessage({
            defaultMessage: 'Pay range',
            description: 'Pay range of the job',
          })}
          value={payDisplay}
        />

        <div className="justify-self-end">
          <EditButton
            toUrl={jobSettingsUrl}
            onClick={() => onRequestRefreshBanner?.()}
          />
        </div>
      </div>

      <div className="bg-fx-neutral-200 h-px w-full" />

      {/* Location, Brand */}
      <div className="grid grid-cols-[auto_1fr] items-center gap-6">
        <PropertyItem
          title={intl.formatMessage({
            defaultMessage: 'Location',
            description: 'Location of the job',
          })}
          value={opening.location_address}
        />

        <div className="justify-self-end">
          <EditButton
            toUrl={locationSettingsUrl}
            onClick={() => onRequestRefreshBanner?.()}
          />
        </div>
      </div>

      <PropertyItem
        title={intl.formatMessage({
          defaultMessage: 'Brand',
          description: 'Brand name of the job',
        })}
        value={opening.brand_name}
      />

      <div className="bg-fx-neutral-200 h-px w-full" />

      <DescriptionSection openingId={opening.external_id} />

      {selectedOpeningIds.length > 1 && (
        <div className="flex justify-between gap-6">
          <Button
            type="button"
            variant="ghost"
            className="!text-fx-neutral-600"
            onClick={removeOpeningFromPosting}
          >
            <FormattedMessage
              defaultMessage="Remove from posting"
              description="Remove the channel from the job posting"
            />
          </Button>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancelClick}
            >
              <FormattedMessage
                defaultMessage="Cancel"
                description="Cancel the channel changes"
              />
            </Button>
            <Button type="button" onClick={handleSaveClick} disabled={hasError}>
              {isUpdatingOpening && <Loader2 className="animate-spin" />}
              <FormattedMessage
                defaultMessage="Save"
                description="Save the channel changes"
              />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function PropertyItem({
  title,
  value,
}: {
  title: string;
  value: string | null;
}) {
  return (
    <div className="flex flex-col gap-1 overflow-hidden">
      <Typography variant="caption" className="!text-fx-neutral-500">
        {title}
      </Typography>
      <Typography variant="body1" className="truncate">
        {value ?? '-'}
      </Typography>
    </div>
  );
}

function EditButton({
  toUrl,
  onClick,
}: {
  toUrl: string;
  onClick?: () => void;
}) {
  return (
    <Button variant="secondary" className="w-20" asChild>
      <Link
        to={toUrl}
        target="_blank"
        onClick={() => {
          onClick?.();
        }}
      >
        <ExternalLinkIcon className="text-fx-primary" />
        <FormattedMessage defaultMessage="Edit" description="Edit button" />
      </Link>
    </Button>
  );
}
